import { useEffect, useRef, useState } from 'react';
import { getConfig } from '../config';
import { Event } from '@podium/browser';

import {
    AdObj,
    AdError,
    Status,
    DefineTagOptions,
    Keywords,
    Finn,
    BannerGlobal,
} from '../../shared/types/ast';

import { Placement, Size as NRMSize } from '../../shared/types/reklameMaster';
import { inview } from '../utils/lazy';
import { cachePlaceholderAd } from './cached-ad-placeholder';
import { peek, subscribe, unsubscribe } from '../podlet/events';
import { displayAdsLog } from '../utils/debug';
import {
    debouncedLoadTagsSafe,
    defineTag,
    onAdAvailable,
    onAdBadRequest,
    onAdLoaded,
    onAdNoBid,
    onAdRequested,
    pushToAnqSafe,
    showTag,
} from '../podlet/ast';
import { countObjectPageBannerLoad } from '../utils/logger';

function processedNrmSizes(
    nrmSizes: NRMSize[],
    skyscraperOnly: boolean = false,
) {
    function arrayEquals(a: number[], b: number[]) {
        return (
            Array.isArray(a) &&
            Array.isArray(b) &&
            a.length === b.length &&
            a.every((val, index) => val === b[index])
        );
    }
    const sizes = nrmSizes.map((size: NRMSize) => [size.width, size.height]);
    if (skyscraperOnly) {
        return sizes.filter((size) => !arrayEquals(size, [180, 700]));
    }
    return sizes;
}

export function tag(
    instanceId: string,
    targetId: string,
    skyscraperOnly?: boolean,
): DefineTagOptions | undefined {
    displayAdsLog(
        ` tag() instanceId ${instanceId} targetId ${targetId} skyscraperOnly ${skyscraperOnly}`,
    );
    let config = getConfig();

    let nrmTag;
    const placementConfig = config?.placements?.find(
        (p: Placement) => p.placementId === instanceId,
    );

    displayAdsLog(`placementConfig ${JSON.stringify(placementConfig)}`);

    nrmTag = !placementConfig?.inventoryCode
        ? undefined //a way of filtering nulls
        : {
              targetId,
              sizes: processedNrmSizes(placementConfig?.sizes, skyscraperOnly),
              keywords: placementConfig?.keywords as Keywords,
              invCode: placementConfig?.inventoryCode,
              allowedFormats: placementConfig?.allowedFormats,
              native: placementConfig?.native,
          };
    displayAdsLog(`nrmTag ${JSON.stringify(nrmTag)}`);

    return nrmTag as DefineTagOptions;
}

function initialTargetId(id: string) {
    const existingCycle = peek('banner', 'refresh');
    if (existingCycle) {
        const cycle = existingCycle.payload;
        return id + cycle;
    }
    return id;
}

function getUUID() {
    return Math.random().toString(36).substring(2, 15);
}

export function useAd(
    id: string,
    skyscraperOnly?: boolean,
    customTag?: DefineTagOptions,
) {
    const [loaded, setLoaded] = useState(false);
    const [status, setStatus] = useState<Status>('requested');
    const [adObj2, setAdObj] = useState<AdObj>();
    const [error, setError] = useState<AdError>();
    const [targetId, setTargetId] = useState(initialTargetId(id));
    const tagConfig = customTag || tag(id, targetId, skyscraperOnly);
    displayAdsLog(`customTag ${JSON.stringify(tagConfig)}`);

    const pulseTrackUUID = useRef(getUUID());

    const sizes = tagConfig?.sizes;

    useEffect(() => {
        if (typeof window === 'object') {
            displayAdsLog(`useAd: window === object`);

            window.FINN = window.FINN || ({} as Finn);
            window.FINN.banner = window.FINN.banner || ({} as BannerGlobal);
            window.FINN.banner.queue = window.FINN.banner.queue || [];
            window.FINN.banner.queue.push(() => {
                if (window.apntag) {
                    onAdRequested(targetId, () => {
                        setStatus('requested');
                        countObjectPageBannerLoad(
                            pulseTrackUUID.current,
                            targetId,
                            'Requested',
                        );
                    });

                    onAdAvailable(targetId, (adObj) => {
                        setAdObj(adObj);
                        setLoaded(true);
                        setStatus('available');
                        countObjectPageBannerLoad(
                            pulseTrackUUID.current,
                            adObj.targetId,
                            'Available',
                            adObj?.creativeId?.toString(),
                        );
                    });

                    onAdLoaded(targetId, (adObj) => {
                        setAdObj(adObj);
                        setStatus('loaded');

                        countObjectPageBannerLoad(
                            pulseTrackUUID.current,
                            adObj.targetId,
                            'Load',
                            adObj?.creativeId?.toString(),
                        );

                        // Test for board placements in search results and cache it
                        const isNativeOrBannerAd =
                            adObj.adType === 'native' ||
                            adObj.adType === 'banner';

                        if (targetId.includes('board_') && isNativeOrBannerAd) {
                            setTimeout(() => {
                                const element =
                                    document.getElementById(targetId);
                                cachePlaceholderAd(
                                    targetId,
                                    element,
                                    adObj.adType,
                                );
                            }, 1000);
                        }
                    });

                    onAdNoBid(targetId, () => {
                        countObjectPageBannerLoad(
                            pulseTrackUUID.current,
                            targetId,
                            'NoBid',
                            adObj2?.creativeId?.toString() || '',
                        );

                        setLoaded(true);
                        setStatus('failed');
                    });

                    onAdBadRequest(targetId, (error) => {
                        countObjectPageBannerLoad(
                            pulseTrackUUID.current,
                            targetId,
                            'Error',
                            adObj2?.creativeId?.toString() || '',
                        );

                        setLoaded(true);
                        setError(error);
                        setStatus('failed');
                    });
                }

                if (tagConfig) {
                    displayAdsLog(`debouncedLoadTagsSafe FROM ad.ts`);
                    pushToAnqSafe(() => {
                        defineTag(tagConfig);
                    });
                    debouncedLoadTagsSafe();
                }
            });
        } else {
            setLoaded(true);
            setStatus('failed');
        }

        function refreshCallback(event: Event) {
            const cycle = event.payload;
            if (cycle) {
                displayAdsLog(
                    `refreshCallback() setTargetId () ${JSON.stringify(
                        id + cycle,
                    )}`,
                );
                setTargetId(id + cycle);
            }
        }

        subscribe('banner', 'refresh', refreshCallback);
        return function cleanup() {
            setAdObj(undefined);
            if (window.apntag && window.apntag.anq) {
                window.apntag.anq.push(() => {
                    if (window.apntag) {
                        window.apntag.offEvent('adAvailable', targetId);
                        window.apntag.offEvent('adLoaded', targetId);
                        window.apntag.offEvent('adNoBid', targetId);
                        window.apntag.offEvent('adBadRequest', targetId);
                    }
                });
            }
            unsubscribe('banner', 'refresh', refreshCallback);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetId]); // We only want this to run when targetId changes

    return {
        adObj: adObj2,
        loaded,
        error,
        status,
        targetId,
        sizes,
    };
}

export function useShowTag(id: string, proximity = 100, threshold = 0.0) {
    useEffect(() => {
        let cleanup = () => {};

        const loadElement = (ele: Element) => {
            const tagId = ele.getAttribute('id');
            if (tagId) {
                // Loads search result ad
                pushToAnqSafe(() => {
                    showTag(tagId);
                });
            }
        };

        pushToAnqSafe(() => {
            const element = document.getElementById(id);
            if (element) {
                cleanup = inview(element, loadElement, proximity, threshold);
            }
        });

        return cleanup;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]); // We only want this to run when id changes
    return {};
}
